import { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, CheckCircle2, Download, Loader2, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import {
  loadKitAcquisition,
  loadKitAlertRules,
  loadKitModelAssignment,
  saveKitAcquisition,
  saveKitAlertRules,
  saveKitModelAssignment,
  type KitAcquisitionState,
  type KitModelAssignment,
  type VisionAlertRule,
  type VisionLabProjectOption
} from '@/app/services/kit-vision-runtime-service';
import { downloadKitEdgeAgent } from '@/app/services/kit-edge-agent-download';

const inputClass = 'h-10 w-full rounded-lg border border-border bg-background px-3 text-sm text-foreground outline-none focus:border-primary focus:ring-2 focus:ring-primary/20';
const defaultRule = (classLabel = ''): VisionAlertRule => ({
  name: classLabel ? `Alerta ${classLabel}` : 'Nova regra', class_label: classLabel,
  min_confidence: 0.6, metric: 'presence', min_metric: null, max_metric: null,
  min_count: 1, persistence_hits: 3, persistence_window: 5, cooldown_seconds: 300,
  severity: 'medium', enabled: true, title: null, description: null
});

export function KitVisionRuntimeControls({ kitId, onAssignmentChange }: {
  kitId: string;
  onAssignmentChange?: (assignment: KitModelAssignment) => void;
}) {
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [projects, setProjects] = useState<VisionLabProjectOption[]>([]);
  const [assignment, setAssignment] = useState<KitModelAssignment | null>(null);
  const [acquisition, setAcquisition] = useState<KitAcquisitionState | null>(null);
  const [rules, setRules] = useState<VisionAlertRule[]>([]);
  const [model, setModel] = useState<{ classes: string[]; version: number; edge_deployable: boolean } | null>(null);

  const selectedProject = useMemo(() => projects.find((project) => project.id === assignment?.vision_lab_project_id) ?? null, [projects, assignment]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    Promise.all([loadKitModelAssignment(kitId), loadKitAcquisition(kitId), loadKitAlertRules(kitId)])
      .then(([modelPayload, acquisitionPayload, rulesPayload]) => {
        if (!active) return;
        setProjects(modelPayload.projects);
        setAssignment(modelPayload.assignment);
        setAcquisition(acquisitionPayload.acquisition);
        setRules(rulesPayload.rules);
        setModel(rulesPayload.model);
      })
      .catch((error) => active && toast.error(error?.message ?? 'Não foi possível carregar a configuração do Kit.'))
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [kitId]);

  const saveModel = async () => {
    if (!assignment) return;
    try {
      setBusy('model');
      const payload = await saveKitModelAssignment(kitId, assignment);
      setAssignment(payload.assignment);
      onAssignmentChange?.(payload.assignment);
      const refreshed = await loadKitAlertRules(kitId);
      setModel(refreshed.model);
      setRules(refreshed.rules);
      toast.success('Modelo de inferência associado ao Kit.');
    } catch (error: any) { toast.error(error?.message ?? 'Não foi possível guardar o modelo.'); }
    finally { setBusy(null); }
  };

  const toggleAcquisition = async (kind: 'video' | 'sensors') => {
    if (!acquisition) return;
    try {
      setBusy(kind);
      const payload = await saveKitAcquisition(kitId, kind === 'video'
        ? { video_enabled: !acquisition.video.enabled }
        : { sensors_enabled: !acquisition.sensors.enabled });
      setAcquisition(payload.acquisition);
      toast.success('Estado de aquisição atualizado.');
    } catch (error: any) { toast.error(error?.message ?? 'Não foi possível alterar a aquisição.'); }
    finally { setBusy(null); }
  };

  const saveRules = async () => {
    try {
      setBusy('rules');
      const payload = await saveKitAlertRules(kitId, rules);
      setRules(payload.rules);
      setModel(payload.model);
      toast.success('Regras de alerta guardadas.');
    } catch (error: any) { toast.error(error?.message ?? 'Não foi possível guardar as regras.'); }
    finally { setBusy(null); }
  };

  const downloadAgent = async () => {
    try {
      setBusy('agent');
      await downloadKitEdgeAgent(kitId);
      toast.success('Pacote do agente preparado para esta unidade.');
    } catch (error: any) {
      toast.error(error?.message ?? 'Não foi possível preparar o agente.');
    } finally {
      setBusy(null);
    }
  };

  if (loading) return <div className="flex items-center justify-center rounded-xl border border-border p-8 text-sm text-muted-foreground"><Loader2 className="mr-2 h-4 w-4 animate-spin" />A carregar integração Vision Lab…</div>;

  return <div className="space-y-4 pt-2">
    <section className="rounded-xl border border-sky-500/40 bg-sky-500/5 p-4">
      <h3 className="font-semibold text-foreground">Modelo de inferência</h3>
      <p className="mt-1 text-xs text-muted-foreground">A versão e o ficheiro privado são resolvidos pelo servidor. ONNX pode executar no browser; PT permanece no Edge.</p>
      <label className="mt-4 block text-sm font-medium">Projeto do AQL Vision Lab</label>
      <select className={`${inputClass} mt-2`} value={assignment?.vision_lab_project_id ?? ''} onChange={(event) => setAssignment((current) => current ? { ...current, vision_lab_project_id: event.target.value || null, vision_model_version: null } : current)}>
        <option value="">Sem modelo associado</option>
        {projects.map((project) => <option key={project.id} value={project.id}>{project.name}{project.study_object ? ` · ${project.study_object}` : ''}</option>)}
      </select>
      <label className="mt-3 block text-sm font-medium">Política da versão</label>
      <select className={`${inputClass} mt-2`} value={assignment?.vision_model_policy ?? 'latest_approved'} onChange={(event) => setAssignment((current) => current ? { ...current, vision_model_policy: event.target.value as 'latest_approved' | 'pinned', vision_model_version: null } : current)}>
        <option value="latest_approved">Usar sempre a última versão aprovada</option>
        <option value="pinned">Fixar uma versão aprovada</option>
      </select>
      {assignment?.vision_model_policy === 'pinned' && selectedProject ? <select className={`${inputClass} mt-3`} value={assignment.vision_model_version ?? ''} onChange={(event) => setAssignment((current) => current ? { ...current, vision_model_version: Number(event.target.value) || null } : current)}>
        <option value="">Selecionar versão…</option>
        {selectedProject.versions.map((version) => <option key={version.version} value={version.version}>v{version.version} · {version.edge_status}</option>)}
      </select> : null}
      <Button type="button" className="mt-4 w-full" onClick={saveModel} disabled={busy === 'model'}>{busy === 'model' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}Guardar modelo</Button>
    </section>

    {acquisition ? <section className="rounded-xl border border-emerald-500/35 bg-emerald-500/5 p-4">
      <h3 className="font-semibold">Aquisição do Kit</h3>
      <p className="mt-1 text-xs text-muted-foreground">O heartbeat mantém-se ativo quando a aquisição é parada.</p>
      <div className="mt-4 grid gap-2 sm:grid-cols-2">
        {(['video', 'sensors'] as const).map((kind) => {
          const state = acquisition[kind];
          const label = kind === 'video' ? 'Vídeo' : 'Sensores';
          return <Button key={kind} type="button" variant="outline" disabled={!state.available || busy === kind} onClick={() => toggleAcquisition(kind)} className={state.enabled ? 'border-emerald-500/50 text-emerald-600' : 'border-amber-500/50 text-amber-600'}>
            {busy === kind ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : state.enabled ? <CheckCircle2 className="mr-2 h-4 w-4" /> : <AlertTriangle className="mr-2 h-4 w-4" />}{label}: {state.available ? state.enabled ? 'Em aquisição' : 'Parado' : 'Sem equipamento'}
          </Button>;
        })}
      </div>
    </section> : null}

    <section className="rounded-xl border border-amber-500/40 bg-amber-500/5 p-4">
      <h3 className="font-semibold">Regras de alerta por inferência</h3>
      <p className="mt-1 text-xs text-muted-foreground">{model ? `Modelo v${model.version} · ${model.classes.length} classes disponíveis.` : 'Associa primeiro um projeto do AQL Vision Lab para obter as classes.'}</p>
      <div className="mt-4 space-y-3">
        {rules.map((rule, index) => <div key={rule.rule_id ?? index} className="grid gap-2 rounded-lg border border-border bg-background/60 p-3 sm:grid-cols-2">
          <input className={inputClass} value={rule.name} placeholder="Nome da regra" onChange={(event) => setRules((current) => current.map((item, itemIndex) => itemIndex === index ? { ...item, name: event.target.value } : item))} />
          <select className={inputClass} value={rule.class_label} onChange={(event) => setRules((current) => current.map((item, itemIndex) => itemIndex === index ? { ...item, class_label: event.target.value } : item))}>
            <option value="">Selecionar classe…</option>{(model?.classes ?? [rule.class_label]).filter(Boolean).map((label) => <option key={label} value={label}>{label}</option>)}
          </select>
          <label className="text-xs text-muted-foreground">Confiança mínima<input className={`${inputClass} mt-1`} type="number" min="0" max="1" step="0.05" value={rule.min_confidence} onChange={(event) => setRules((current) => current.map((item, itemIndex) => itemIndex === index ? { ...item, min_confidence: Number(event.target.value) } : item))} /></label>
          <label className="text-xs text-muted-foreground">Severidade<select className={`${inputClass} mt-1`} value={rule.severity} onChange={(event) => setRules((current) => current.map((item, itemIndex) => itemIndex === index ? { ...item, severity: event.target.value as VisionAlertRule['severity'] } : item))}><option value="critical">Crítica</option><option value="high">Alta</option><option value="medium">Média</option><option value="low">Baixa</option></select></label>
          <div className="flex items-center justify-between sm:col-span-2"><label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={rule.enabled} onChange={(event) => setRules((current) => current.map((item, itemIndex) => itemIndex === index ? { ...item, enabled: event.target.checked } : item))} />Ativa</label><Button type="button" variant="ghost" size="sm" onClick={() => setRules((current) => current.filter((_, itemIndex) => itemIndex !== index))}><Trash2 className="h-4 w-4" /></Button></div>
        </div>)}
      </div>
      <Button type="button" variant="outline" className="mt-3 w-full border-dashed" disabled={!model?.classes.length} onClick={() => setRules((current) => [...current, defaultRule(model?.classes[0] ?? '')])}><Plus className="mr-2 h-4 w-4" />Adicionar regra</Button>
      <Button type="button" className="mt-3 w-full" disabled={busy === 'rules'} onClick={saveRules}>{busy === 'rules' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}Guardar regras de alerta</Button>
    </section>

    <Button type="button" variant="outline" className="h-12 w-full rounded-xl" disabled={busy === 'agent'} onClick={() => void downloadAgent()}>
      {busy === 'agent' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />}
      {busy === 'agent' ? 'A preparar agente…' : 'Descarregar agente'}
    </Button>
  </div>;
}
